import React from "react";
import { Game, GameType } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";
import { useSetAtom } from "jotai";
import "./FeaturedGameSection.scss";
import { formatPlayCount } from "../../../../utils/helpers";
import HumanIcon from "../../../icons/HumanIcon";
import PlayIconFlilled from "../../../icons/PlayIconFlilled";
import PrimaryActionButton from "../../../Buttons/PrimaryActionButton/PrimaryActionButton";
import Skeleton from "../../../Skeleton/Skeleton";
import ImageFallbackThumbnail from "../../../../images/fallback-thumbnail.png";
import { ThemeVariant } from "../../../../utils/constants/generic-types";
import useSubscription from "../../../../react-query/queries/use-subscription-status";
import useGamesQuery from "../../../../react-query/queries/use-games";
import { gameDetailsDrawerSlugAtom } from "../../../../jotai/atoms/games";

const SKELETON_SHIMMER_STYLE = { background: "#1b0c3dff" };
const SKELETON_BG_STYLE = { background: "rgba(51, 23, 120, 0.1)" };

function FeaturedGameSectionSkeleton() {
  return (
    <section className="featured-game-section px-3">
      <Skeleton
        className="featured-game-heading-skeleton"
        width={140}
        height={18}
        borderRadius={4}
        shimmerStyle={SKELETON_SHIMMER_STYLE}
        style={SKELETON_BG_STYLE}
        theme={ThemeVariant.DARK}
      />
      <div className="featured-game-card featured-game-card-skeleton">
        <div className="featured-game-content">
          <div className="featured-game-info">
            <Skeleton
              width={96}
              height={14}
              borderRadius={4}
              shimmerStyle={SKELETON_SHIMMER_STYLE}
              style={SKELETON_BG_STYLE}
              theme={ThemeVariant.DARK}
            />
            <Skeleton
              width={180}
              height={24}
              borderRadius={4}
              shimmerStyle={SKELETON_SHIMMER_STYLE}
              style={SKELETON_BG_STYLE}
              theme={ThemeVariant.DARK}
            />
          </div>
          <Skeleton
            width="100%"
            height={40}
            borderRadius={100}
            shimmerStyle={SKELETON_SHIMMER_STYLE}
            style={SKELETON_BG_STYLE}
            theme={ThemeVariant.DARK}
          />
        </div>
      </div>
    </section>
  );
}

function FeaturedGameCard({
  game,
  heading,
  onPlay,
}: {
  game: Game;
  heading: string;
  onPlay: () => void;
}) {
  const { t } = useTranslation("dashboard");
  const hasPlayCount = game.playCount != null && game.playCount > 0;

  return (
    <section className="featured-game-section px-3">
      <h2 className="heading">{heading}</h2>
      <div
        className="featured-game-card"
        style={{
          backgroundImage: `url(${game.thumbnail || ImageFallbackThumbnail})`,
        }}
        aria-label={game.name}
      >
        <div className="featured-game-overlay" />
        <div className="featured-game-content">
          <div className="featured-game-info">
            {hasPlayCount && (
              <span className="playing-count">
                <HumanIcon width={16} height={16} />
                {`${formatPlayCount(game.playCount!)} playing`}
              </span>
            )}
            <h3 className="game-name">{game.name}</h3>
          </div>
          <PrimaryActionButton
            buttonClassName="start-playing-btn"
            onClick={onPlay}
            text={
              <span className="d-inline-flex align-items-center justify-content-center gap-1">
                <PlayIconFlilled width={16} height={16} fill="#ffffff" />
                <span>{t("start_playing")}</span>
              </span>
            }
          />
        </div>
      </div>
    </section>
  );
}

function FeaturedGameSection() {
  const { t } = useTranslation("dashboard");
  const setDrawerSlug = useSetAtom(gameDetailsDrawerSlugAtom);
  const { isSubscribed, isLoading: isSubscriptionLoading } = useSubscription();

  const gameType = isSubscribed ? GameType.FEATURED : GameType.FREE;

  const { data, isLoading: isGamesLoading } = useGamesQuery({
    limit: 1,
    gameType,
    enabled: !isSubscriptionLoading,
  });

  if (isSubscriptionLoading || isGamesLoading)
    return <FeaturedGameSectionSkeleton />;

  const featuredGame = data?.records?.[0];
  if (!featuredGame) return null;

  const heading = isSubscribed ? t("featured_game") : t("free_game");

  return (
    <FeaturedGameCard
      game={featuredGame}
      heading={heading}
      onPlay={() => {
        if (featuredGame.slug) {
          setDrawerSlug(featuredGame.slug);
        }
      }}
    />
  );
}

export default FeaturedGameSection;
